import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import RichTextEditor from '../../components/admin/RichTextEditor';
import { 
  ArrowLeft, 
  Save, 
  Loader2,
  Plus,
  Trash2,
  GripVertical,
  Languages,
  Image,
  Type,
  Link as LinkIcon,
  Clock,
  Utensils,
  MapPin,
  ChevronDown,
  ChevronUp,
  Wand2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const languages = [
  { code: 'ca', name: 'Català', flag: '🇦🇩' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
];

const blockTypes = [
  { type: 'text', label: 'Text', icon: Type, description: 'Bloc de text amb títol i cos' },
  { type: 'image', label: 'Imatge', icon: Image, description: 'Imatge amb peu de foto' },
  { type: 'cta', label: 'CTA', icon: LinkIcon, description: 'Botó amb enllaç' },
  { type: 'schedule', label: 'Horaris', icon: Clock, description: 'Horaris i preus' },
  { type: 'menu', label: 'Menú', icon: Utensils, description: 'Menú restaurant' },
  { type: 'contact', label: 'Contacte', icon: MapPin, description: 'Dades de contacte' },
];

export const PageEditor = () => {
  const { pageKey, lang } = useParams();
  const navigate = useNavigate();
  const { isAdmin, authFetch } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [content, setContent] = useState({
    title: '',
    subtitle: '',
    hero_image: '',
    meta_title: '',
    meta_description: '',
    content_blocks: []
  });
  const [expandedBlocks, setExpandedBlocks] = useState({});

  const currentLang = languages.find(l => l.code === lang) || languages[0];

  useEffect(() => {
    fetchContent();
  }, [pageKey, lang]);

  const fetchContent = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/content/${pageKey}/${lang}`, {
        
      });
      if (response.ok) {
        const data = await response.json();
        setContent({
          title: data.title || '',
          subtitle: data.subtitle || '',
          hero_image: data.hero_image || '',
          meta_title: data.meta_title || '',
          meta_description: data.meta_description || '',
          content_blocks: data.content_blocks || []
        });
        // Expand all blocks by default
        const expanded = {};
        (data.content_blocks || []).forEach(block => {
          expanded[block.id] = true;
        });
        setExpandedBlocks(expanded);
      }
    } catch (error) {
      console.error('Failed to fetch content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API_URL}/api/content/${pageKey}/${lang}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify(content)
      });

      if (response.ok) {
        toast.success('Contingut guardat correctament');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error guardant contingut');
      }
    } catch (error) {
      toast.error('Error guardant contingut');
    } finally {
      setSaving(false);
    }
  };

  const handleTranslate = async (text, fieldPath) => {
    if (!text) return;
    
    setTranslating(true);
    try {
      const response = await authFetch(`${API_URL}/api/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({
          text,
          source_language: lang,
          target_languages: languages.filter(l => l.code !== lang).map(l => l.code)
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Traducció generada (esborrany). Recorda revisar-la.');
        return data.translations;
      } else {
        toast.error('Error generant traducció');
      }
    } catch (error) {
      toast.error('Error generant traducció');
    } finally {
      setTranslating(false);
    }
    return null;
  };

  const addBlock = (type) => {
    const newBlock = {
      id: `block_${Date.now()}`,
      block_type: type,
      content: getDefaultContent(type),
      order: content.content_blocks.length,
      is_editable_by_editor: true
    };
    
    setContent(prev => ({
      ...prev,
      content_blocks: [...prev.content_blocks, newBlock]
    }));
    
    setExpandedBlocks(prev => ({ ...prev, [newBlock.id]: true }));
  };

  const getDefaultContent = (type) => {
    switch (type) {
      case 'text':
        return { title: '', body: '' };
      case 'image':
        return { url: '', caption: '', alt: '' };
      case 'cta':
        return { text: '', url: '', style: 'primary' };
      case 'schedule':
        return { items: [{ day: '', hours: '', price: '' }] };
      case 'menu':
        return { sections: [{ name: '', items: [{ name: '', price: '', description: '' }] }] };
      case 'contact':
        return { phone: '', email: '', address: '' };
      default:
        return {};
    }
  };

  const updateBlock = (blockId, newContent) => {
    setContent(prev => ({
      ...prev,
      content_blocks: prev.content_blocks.map(block =>
        block.id === blockId ? { ...block, content: newContent } : block
      )
    }));
  };

  const removeBlock = (blockId) => {
    if (!window.confirm('Segur que vols eliminar aquest bloc?')) return;
    
    setContent(prev => ({
      ...prev,
      content_blocks: prev.content_blocks.filter(block => block.id !== blockId)
    }));
  };

  const moveBlock = (blockId, direction) => {
    const blocks = [...content.content_blocks];
    const index = blocks.findIndex(b => b.id === blockId);
    if (direction === 'up' && index > 0) {
      [blocks[index], blocks[index - 1]] = [blocks[index - 1], blocks[index]];
    } else if (direction === 'down' && index < blocks.length - 1) {
      [blocks[index], blocks[index + 1]] = [blocks[index + 1], blocks[index]];
    }
    blocks.forEach((block, i) => block.order = i);
    setContent(prev => ({ ...prev, content_blocks: blocks }));
  };

  const toggleBlock = (blockId) => {
    setExpandedBlocks(prev => ({ ...prev, [blockId]: !prev[blockId] }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="page-editor">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/pages')}
              className="p-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-display font-semibold text-[#1A4D2E]">
                Editar: {pageKey}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-lg">{currentLang.flag}</span>
                <span className="text-[#666666]">{currentLang.name}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Language switcher */}
            <div className="flex gap-1 bg-[#F5F5F5] p-1 rounded-lg">
              {languages.map((l) => (
                <button
                  key={l.code}
                  onClick={() => navigate(`/admin/pages/${pageKey}/${l.code}`)}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    l.code === lang 
                      ? 'bg-white text-[#1A4D2E] shadow-sm' 
                      : 'text-[#666666] hover:text-[#333333]'
                  }`}
                >
                  {l.flag} {l.code.toUpperCase()}
                </button>
              ))}
            </div>
            
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#1A4D2E] hover:bg-[#153d25] text-white"
              data-testid="save-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar
            </Button>
          </div>
        </div>

        {/* Page meta info */}
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-6 space-y-4">
          <h2 className="font-semibold text-[#333333]">Informació de la pàgina</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Títol</Label>
              <Input
                value={content.title}
                onChange={(e) => setContent(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Títol de la pàgina"
                data-testid="page-title"
              />
            </div>
            <div className="space-y-2">
              <Label>Subtítol</Label>
              <Input
                value={content.subtitle}
                onChange={(e) => setContent(prev => ({ ...prev, subtitle: e.target.value }))}
                placeholder="Subtítol opcional"
              />
            </div>
          </div>

          {isAdmin && (
            <div className="space-y-2">
              <Label>Imatge Hero (només Admin)</Label>
              <Input
                value={content.hero_image}
                onChange={(e) => setContent(prev => ({ ...prev, hero_image: e.target.value }))}
                placeholder="URL de la imatge hero"
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Meta Títol (SEO)</Label>
              <Input
                value={content.meta_title}
                onChange={(e) => setContent(prev => ({ ...prev, meta_title: e.target.value }))}
                placeholder="Títol per SEO"
              />
            </div>
            <div className="space-y-2">
              <Label>Meta Descripció (SEO)</Label>
              <Input
                value={content.meta_description}
                onChange={(e) => setContent(prev => ({ ...prev, meta_description: e.target.value }))}
                placeholder="Descripció per SEO"
              />
            </div>
          </div>
        </div>

        {/* Content blocks */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-[#333333]">Blocs de contingut</h2>
          </div>

          {/* Existing blocks */}
          <div className="space-y-3">
            {content.content_blocks.map((block, index) => (
              <BlockEditor
                key={block.id}
                block={block}
                isExpanded={expandedBlocks[block.id]}
                onToggle={() => toggleBlock(block.id)}
                onUpdate={(newContent) => updateBlock(block.id, newContent)}
                onRemove={() => removeBlock(block.id)}
                onMoveUp={() => moveBlock(block.id, 'up')}
                onMoveDown={() => moveBlock(block.id, 'down')}
                canMoveUp={index > 0}
                canMoveDown={index < content.content_blocks.length - 1}
                onTranslate={handleTranslate}
                isTranslating={translating}
                isAdmin={isAdmin}
              />
            ))}
          </div>

          {/* Add block buttons */}
          <div className="bg-[#F9F9F7] border border-dashed border-[#E5E5E5] rounded-lg p-6">
            <p className="text-sm text-[#888888] mb-4">Afegir nou bloc:</p>
            <div className="flex flex-wrap gap-2">
              {blockTypes.map((bt) => (
                <Button
                  key={bt.type}
                  variant="outline"
                  onClick={() => addBlock(bt.type)}
                  className="gap-2"
                  data-testid={`add-block-${bt.type}`}
                >
                  <bt.icon className="w-4 h-4" />
                  {bt.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

const BlockEditor = ({ 
  block, 
  isExpanded, 
  onToggle, 
  onUpdate, 
  onRemove, 
  onMoveUp, 
  onMoveDown,
  canMoveUp,
  canMoveDown,
  onTranslate,
  isTranslating,
  isAdmin
}) => {
  const blockType = blockTypes.find(bt => bt.type === block.block_type);
  const Icon = blockType?.icon || Type;

  const renderEditor = () => {
    switch (block.block_type) {
      case 'text':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Títol</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onTranslate(block.content.title)}
                  disabled={isTranslating || !block.content.title}
                  className="text-xs gap-1"
                >
                  <Wand2 className="w-3 h-3" />
                  Traduir
                </Button>
              </div>
              <Input
                value={block.content.title || ''}
                onChange={(e) => onUpdate({ ...block.content, title: e.target.value })}
                placeholder="Títol del bloc"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Contingut</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onTranslate(block.content.body)}
                  disabled={isTranslating || !block.content.body}
                  className="text-xs gap-1"
                >
                  <Wand2 className="w-3 h-3" />
                  Traduir
                </Button>
              </div>
              <RichTextEditor
                value={block.content.body || ''}
                onChange={(value) => onUpdate({ ...block.content, body: value })}
                placeholder="Contingut del bloc..."
                minHeight={150}
              />
            </div>
          </div>
        );

      case 'image':
        return (
          <div className="space-y-4">
            {/* Image format instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
              <p className="font-semibold mb-1">Format recomanat:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Mida: 800×600 px (proporció 4:3) per a contingut general</li>
                <li>Hero: 1920×700 px per a imatges de capçalera</li>
                <li>Format: JPG o WebP, màxim 200KB</li>
                <li>Puja a <a href="https://imgur.com" target="_blank" rel="noopener noreferrer" className="underline">Imgur</a> o utilitza URLs directes</li>
              </ul>
            </div>
            <div className="space-y-2">
              <Label>URL de la imatge</Label>
              <Input
                value={block.content.url || ''}
                onChange={(e) => onUpdate({ ...block.content, url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            {block.content.url && (
              <img 
                src={block.content.url} 
                alt={block.content.alt || ''} 
                className="max-h-40 rounded border"
              />
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Peu de foto</Label>
                <Input
                  value={block.content.caption || ''}
                  onChange={(e) => onUpdate({ ...block.content, caption: e.target.value })}
                  placeholder="Descripció de la imatge"
                />
              </div>
              <div className="space-y-2">
                <Label>Text alternatiu (accessibilitat i SEO)</Label>
                <Input
                  value={block.content.alt || ''}
                  onChange={(e) => onUpdate({ ...block.content, alt: e.target.value })}
                  placeholder="Descripció per a cercadors i accessibilitat"
                />
              </div>
            </div>
          </div>
        );

      case 'cta':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Text del botó</Label>
                <Input
                  value={block.content.text || ''}
                  onChange={(e) => onUpdate({ ...block.content, text: e.target.value })}
                  placeholder="Ex: Reserva ara"
                />
              </div>
              <div className="space-y-2">
                <Label>Estil</Label>
                <select
                  value={block.content.style || 'primary'}
                  onChange={(e) => onUpdate({ ...block.content, style: e.target.value })}
                  className="w-full border border-[#E5E5E5] rounded-md px-3 py-2"
                >
                  <option value="primary">Primari (verd)</option>
                  <option value="secondary">Secundari (blanc)</option>
                  <option value="outline">Contorn</option>
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>URL de destí</Label>
              <Input
                value={block.content.url || ''}
                onChange={(e) => onUpdate({ ...block.content, url: e.target.value })}
                placeholder="https://..."
              />
            </div>
          </div>
        );

      case 'schedule':
        return (
          <div className="space-y-4">
            <Label>Horaris</Label>
            {(block.content.items || []).map((item, i) => (
              <div key={i} className="grid grid-cols-3 gap-2">
                <Input
                  value={item.day || ''}
                  onChange={(e) => {
                    const items = [...(block.content.items || [])];
                    items[i] = { ...items[i], day: e.target.value };
                    onUpdate({ ...block.content, items });
                  }}
                  placeholder="Dia/Període"
                />
                <Input
                  value={item.hours || ''}
                  onChange={(e) => {
                    const items = [...(block.content.items || [])];
                    items[i] = { ...items[i], hours: e.target.value };
                    onUpdate({ ...block.content, items });
                  }}
                  placeholder="Horari"
                />
                <div className="flex gap-2">
                  <Input
                    value={item.price || ''}
                    onChange={(e) => {
                      const items = [...(block.content.items || [])];
                      items[i] = { ...items[i], price: e.target.value };
                      onUpdate({ ...block.content, items });
                    }}
                    placeholder="Preu"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const items = (block.content.items || []).filter((_, idx) => idx !== i);
                      onUpdate({ ...block.content, items });
                    }}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const items = [...(block.content.items || []), { day: '', hours: '', price: '' }];
                onUpdate({ ...block.content, items });
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Afegir fila
            </Button>
          </div>
        );

      case 'contact':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Telèfon</Label>
              <Input
                value={block.content.phone || ''}
                onChange={(e) => onUpdate({ ...block.content, phone: e.target.value })}
                placeholder="+34 661 19 40 23"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                value={block.content.email || ''}
                onChange={(e) => onUpdate({ ...block.content, email: e.target.value })}
                placeholder="info@exemple.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Adreça</Label>
              <Textarea
                value={block.content.address || ''}
                onChange={(e) => onUpdate({ ...block.content, address: e.target.value })}
                placeholder="Adreça completa..."
                rows={2}
              />
            </div>
          </div>
        );

      default:
        return (
          <div className="text-sm text-[#888888]">
            Tipus de bloc no reconegut: {block.block_type}
          </div>
        );
    }
  };

  return (
    <div 
      className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden"
      data-testid={`block-${block.id}`}
    >
      {/* Block header */}
      <div 
        className="flex items-center justify-between px-4 py-3 bg-[#F9F9F7] cursor-pointer hover:bg-[#F5F5F5]"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <GripVertical className="w-4 h-4 text-[#888888]" />
          <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
            <Icon className="w-4 h-4 text-[#1A4D2E]" />
          </div>
          <span className="font-medium text-sm text-[#333333]">
            {blockType?.label || block.block_type}
          </span>
          {block.content.title && (
            <span className="text-sm text-[#888888] truncate max-w-[200px]">
              — {block.content.title}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
            disabled={!canMoveUp}
          >
            <ChevronUp className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
            disabled={!canMoveDown}
          >
            <ChevronDown className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="text-red-500 hover:text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-[#888888]" />
          ) : (
            <ChevronDown className="w-4 h-4 text-[#888888]" />
          )}
        </div>
      </div>

      {/* Block content */}
      {isExpanded && (
        <div className="p-4 border-t border-[#E5E5E5]">
          {renderEditor()}
        </div>
      )}
    </div>
  );
};

export default PageEditor;
